import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ModifierListRow } from "@/modules/catalog/domain/types";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm } from "@/shared/lib/confirm";
import { Loader } from "@/shared/ui/Loader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";


import { CheckIcon, EditIcon, TrashIcon, XIcon } from "lucide-react";

type ModifiersTableProps = {
  data: ModifierListRow[];
  isLoading?: boolean;
  onEdit: (row: ModifierListRow) => void;
  onDelete: (id: string) => void;
};

export function ModifiersTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: ModifiersTableProps) {
  const { has } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        ບໍ່ພົບຊຸດຕົວເລືອກ
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ຊື່</TableHead>
          <TableHead>ເລືອກຕໍ່າສຸດ / ສູງສຸດ</TableHead>
          <TableHead>ບັງຄັບເລືອກ</TableHead>
          <TableHead>ຈຳນວນຕົວເລືອກ</TableHead>
          <TableHead className="w-[120px]">ການດຳເນີນການ</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => {
          const actions: {
            label: string;
            icon?: React.ReactNode;
            variant?: "destructive";
            onClick: () => void;
          }[] = [];

          if (has("catalog:update")) {
            actions.push({
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="h-4 w-4" />,
              onClick: () => onEdit(row),
            });
          }
          if (has("catalog:delete")) {
            actions.push({
              label: "ລຶບ",
              variant: "destructive",
              icon: <TrashIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບຊຸດຕົວເລືອກ",
                  description:
                    "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຊຸດຕົວເລືອກນີ້?",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onDelete(row.id);
              },
            });
          }

          return (
            <TableRow key={row.id}>
              <TableCell className="font-medium">{row.name}</TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {row.minSelect} – {row.maxSelect}
              </TableCell>
              <TableCell>
                {row.isRequired ? (
                  <CheckIcon className="h-4 w-4 text-green-500" />
                ) : (
                  <XIcon className="h-4 w-4 text-muted-foreground" />
                )}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {row.optionCount}
              </TableCell>
              <TableCell>
                <RowActions actions={actions} maxInline={3} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
